import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-update-available-dialog',
  standalone: true,
  imports: [MatDialogModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *transloco="let t">
      <div class="update-dialog">
        <!-- Avisa, no obliga: el tache cierra SIN recargar. Es el único camino para
             cerrar sin actualizar (el diálogo se abre con disableClose). -->
        <button
          type="button"
          class="update-dialog__close"
          [attr.aria-label]="t('common.close')"
          (click)="onDismiss()"
          data-testid="update-available-close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="update-dialog__icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 15V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 class="update-dialog__title">{{ t('shared.updateAvailable.title') }}</h2>
        <p class="update-dialog__message">{{ t('shared.updateAvailable.message') }}</p>
        <div class="update-dialog__actions">
          <button
            class="update-dialog__btn update-dialog__btn--primary"
            (click)="onUpdate()"
            data-testid="update-available-update"
          >
            {{ t('shared.updateAvailable.update') }}
          </button>
        </div>
      </div>
    </ng-container>
  `,
  styles: [`
    .update-dialog {
      position: relative;
      padding: var(--spacing-lg, 24px);
      text-align: center;
    }
    /* Esquina superior derecha, sobre el relleno: el ícono va centrado y el
       título debajo, así que la caja de 32px no tapa ninguno. */
    .update-dialog__close {
      position: absolute;
      top: 8px;
      right: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      border-radius: var(--border-radius-md, 8px);
      background: transparent;
      color: var(--color-text-secondary);
      cursor: pointer;
    }
    .update-dialog__close:hover {
      color: var(--color-secondary);
    }
    .update-dialog__close:focus-visible {
      outline: 2px solid var(--color-secondary);
      outline-offset: 2px;
    }
    .update-dialog__icon {
      margin-bottom: var(--spacing-md, 16px);
      color: var(--color-secondary, #157BF8);
    }
    .update-dialog__title {
      font-family: var(--font-family-primary, 'Montserrat', sans-serif);
      font-size: 20px;
      font-weight: 600;
      color: var(--color-text-1, #333);
      margin: 0 0 8px;
    }
    .update-dialog__message {
      font-family: var(--font-family-primary, 'Montserrat', sans-serif);
      font-size: 14px;
      color: var(--color-text-2, #666);
      margin: 0 0 24px;
      line-height: 1.5;
    }
    .update-dialog__actions {
      display: flex;
      justify-content: center;
      gap: var(--spacing-sm, 12px);
    }
    .update-dialog__btn {
      font-family: var(--font-family-primary, 'Montserrat', sans-serif);
      font-size: 14px;
      font-weight: 500;
      padding: 10px 24px;
      border-radius: 8px;
      border: none;
      cursor: pointer;
    }
    .update-dialog__btn--primary {
      background: var(--color-secondary, #157BF8);
      color: var(--color-white, #fff);
    }
  `]
})
export class UpdateAvailableDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<UpdateAvailableDialogComponent>);

  onUpdate(): void {
    this.dialogRef.close(true);
  }

  /** Cierra sin actualizar: `AppUpdateService` solo recarga con `true`. */
  onDismiss(): void {
    this.dialogRef.close(false);
  }
}
